import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WMICAddNewDriverButton } from 'wmic-pe-capability-gateway-common-pa-react';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';
import {
    CONSTANTS,
    FlowStepId,
    JobType,
    MODAL_CONSTANTS,
    parseErrorMessage,
    SUITES,
    WMICDriverUtil,
    WMICLogger,
    WMICValidationUtil,
    WMICVariousUtil,
    useCanJumpToWizardStepDispatch,
} from 'wmic-pe-portals-utils-js';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import { useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import { messages as quoteAndBindCommonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import messages from './useDriverActions.messages';

const useDriverActions = params => {
    const { jobVM, updateWizardData, setShowErrors } = params;
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { jobType } = jobVM.value.baseData;
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { EndorsementService } = useDependencies('EndorsementService');
    const { RenewalService } = useDependencies('RenewalService');
    const { initDispatch, wizardStepDispatch } = useCanJumpToWizardStepDispatch();
    const { setWizardLoading, showConfirm, showError } = useWizardModals();
    const [additionalNamedInsureds, setAdditionalNamedInsureds] = useState([]);

    const JobService = useMemo(() => {
        switch (jobType) {
            case JobType.SUBMISSION:
                return LoadSaveService;
            case JobType.POLICY_CHANGE:
                return EndorsementService;
            case JobType.RENEWAL:
                return RenewalService;
            default:
                throw new Error(`Unsupported job type ${jobType}`);
        }
    }, [LoadSaveService, EndorsementService, RenewalService, jobType]);

    const initDriverData = useCallback(() => {
        initDispatch(jobVM);
        const driversTmp = _.get(jobVM, RISK_PATHS.DRIVERS_PATH);
        const additionalNamedInsuredsSub = _.get(jobVM, `${CONSTANTS.ANIS_PATH}.value`);
        const additionalNamedInsuredsTmp = [];

        if (additionalNamedInsuredsSub && additionalNamedInsuredsSub.length > 0) {
            additionalNamedInsuredsSub.forEach(nameInsured => {
                const additionalNameInsured = viewModelService.create(nameInsured, SUITES.PC, CONSTANTS.ANI_DTO_NAME);

                additionalNameInsured.isDriver = false;
                const aniSubType = additionalNameInsured.subtype?.value?.code;

                if (aniSubType === CONSTANTS.Person.toLowerCase()) {
                    const isDriver = driversTmp.value.some(
                        driver => additionalNameInsured.contactPublicID?.value === driver.person?.publicID?.value
                    );

                    additionalNameInsured.isDriver = isDriver;
                }

                additionalNamedInsuredsTmp.push(additionalNameInsured);
            });
            if (additionalNamedInsuredsTmp.length > 0) {
                setAdditionalNamedInsureds(additionalNamedInsuredsTmp);
            }
        }

        const pniTmp = jobVM.baseData.primaryNamedInsured_WMIC?.value;
        pniTmp.isDriver = driversTmp.value.some(driver => pniTmp.contactPublicID === driver.person?.publicID);
    }, [initDispatch, jobVM, viewModelService]);

    const onCreateDriver = useCallback(
        async (index, namedInsured, insuredRelation) => {
            const jobID = jobVM.value.jobID || jobVM.value.quoteID;
            try {
                setWizardLoading(true, translator(messages.addingDriver));
                const driver = WMICDriverUtil.createDriver(namedInsured, insuredRelation, jobVM);
                const newDriverDto = await LoadSaveService.createDriver(jobID, driver, authHeader);
                jobVM.lobData?.personalAuto?.coverables?.drivers?.value?.push(newDriverDto);
                wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.RISK_ADDED });
                return newDriverDto;
            } catch (error) {
                WMICLogger.error('Create driver failed', error);
                WMICErrorHandler.processAsModal(error, jobID, jobType).catch(() => {});
                return false;
            } finally {
                setWizardLoading(false);
            }
        },
        [setWizardLoading, translator, jobVM, LoadSaveService, authHeader, wizardStepDispatch, jobType]
    );

    const onCreateDriverWithModal = useCallback(
        (index, namedInsured, insuredRelation) =>
            showConfirm({
                title: quoteAndBindCommonMessages.revalidationRequiredTitle,
                message: quoteAndBindCommonMessages.revalidationRequiredBody,
                status: MODAL_CONSTANTS.STATUS.WARNING,
                icon: MODAL_CONSTANTS.ICON.ERROR,
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.cancelModel,
            }).then(results => {
                if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                    return false;
                }

                return onCreateDriver(index, namedInsured, insuredRelation);
            }),
        [onCreateDriver, showConfirm]
    );

    const onSaveDriver = useCallback(
        async selectedDriver => {
            delete jobVM.value.bindData;

            try {
                setWizardLoading(true, translator(messages.savingDriver));
                const saveDriverResult = await JobService.saveDriver(selectedDriver.value?.publicID, jobVM.value, authHeader);
                jobVM.value = saveDriverResult;

                updateWizardData(jobVM);
                setShowErrors(false);

                if (WMICValidationUtil.hasDtoValidationErrors(jobVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                }
                wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.RESET, vm: jobVM });
                return true;
            } catch (error) {
                WMICLogger.error('Save driver failed', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() => {
                    // do nothing
                });
                return false;
            } finally {
                if (jobVM.isEditingPage?.value) {
                    jobVM.isEditingPage.value = false;
                }
                setWizardLoading(false);
            }
        },
        [
            jobVM,
            setWizardLoading,
            translator,
            JobService,
            authHeader,
            updateWizardData,
            setShowErrors,
            wizardStepDispatch,
        ]
    );

    const onSaveDriverMiddleware = useCallback(
        selectedDriver => {
            const driversWithDuplicateLicence = WMICDriverUtil.getDriversWithDuplicateLicense(
                _.get(jobVM.value, RISK_PATHS.DRIVERS_PATH)
            );
            if (driversWithDuplicateLicence) {
                const otherDriverWithSameLicense =
                    driversWithDuplicateLicence.find(d => d.publicID !== selectedDriver?.value?.publicID) ||
                    driversWithDuplicateLicence[0];

                showError({
                    title: messages.licenseNotUnique,
                    message: translator(messages.licenceMatchMsg, { driver: otherDriverWithSameLicense.displayName }),
                });
            }
        },
        [jobVM.value, showError, translator]
    );

    const deleteDriver = useCallback(
        async driverId => {
            delete jobVM.value.bindData;

            try {
                setWizardLoading(true, translator(messages.removingDriver));

                let removeDriverResult;
                let removeDriverResultData;
                switch (jobType) {
                    case JobType.SUBMISSION:
                        removeDriverResult = await LoadSaveService.removeDriver(jobVM.value, driverId, authHeader);
                        removeDriverResultData = removeDriverResult.quoteData;
                        break;
                    case JobType.POLICY_CHANGE:
                        removeDriverResult = await EndorsementService.removeDriver(
                            jobVM.jobID.value,
                            driverId,
                            authHeader
                        );
                        removeDriverResultData = removeDriverResult.policyChangeData;
                        break;
                    case JobType.RENEWAL:
                        removeDriverResult = await RenewalService.removeDriver(jobVM.jobID.value, driverId, authHeader);
                        removeDriverResultData = removeDriverResult.renewalData;
                        break;
                    default:
                        break;
                }

                if (removeDriverResult.errorMessages && removeDriverResult.errorMessages.length > 0) {
                    showError({
                        title: messages.removingDriverErrorModalTitle,
                        message: removeDriverResult.errorMessages.join('\n'),
                    });
                    return false;
                }

                jobVM.value = removeDriverResultData;

                updateWizardData(jobVM);
                setShowErrors(false);

                if (jobType === JobType.SUBMISSION) {
                    if (WMICValidationUtil.hasDtoValidationErrors(jobVM, FlowStepId.PA_DRIVERS)) {
                        WMICVariousUtil.scrollToTop();
                    }
                }

                return true;
            } catch (error) {
                WMICLogger.error('Remove driver failed', error);
                WMICErrorHandler.processAsModal(error, jobVM.value.jobID, jobType).catch(() => {});
                return false;
            } finally {
                if (jobVM.isEditingPage?.value) {
                    jobVM.isEditingPage.value = false;
                }
                setWizardLoading(false);
            }
        },
        [
            jobVM,
            setWizardLoading,
            translator,
            jobType,
            updateWizardData,
            setShowErrors,
            LoadSaveService,
            authHeader,
            EndorsementService,
            RenewalService,
            showError,
        ]
    );

    const onDeleteDriver = useCallback(
        driver => {
            const modalMsg = translator(messages.removeDriver, { driver: driver.displayName?.value });

            showConfirm({
                title: messages.removeDriverTitle,
                message: modalMsg,
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.noModel,
            })
                .then(async response => {
                    if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                        return deleteDriver(driver.publicID?.value);
                    }
                    return undefined;
                })
                .catch(() => undefined); // no-op
        },
        [showConfirm, translator, deleteDriver]
    );

    const renderAddDriverButton = useCallback(
        ({ isEditing, onClick }) => (
            <WMICAddNewDriverButton
                additionalNamedInsureds={additionalNamedInsureds}
                isEditing={isEditing}
                jobVM={jobVM}
                onClick={onClick}
            />
        ),
        [additionalNamedInsureds, jobVM]
    );

    return {
        initDriverData,
        onCreateDriver,
        onCreateDriverWithModal,
        onSaveDriver,
        onSaveDriverMiddleware,
        onDeleteDriver,
        renderAddDriverButton,
    };
};

export default useDriverActions;
